<template>
  <div class="setup-ctn">
    <div v-if="initialized" class="pa-5">
      <div class="label font-weight-bold">
        {{$t('setup_default_language')}}
      </div>

      <div class="font-weight-light py-2">
        {{$t('setup_default_language_description1')}}<br>
        {{$t('setup_default_language_description2')}}
      </div>

      <v-autocomplete
        v-model="selectedDefaultLanguage"
        :items="languageDropdown"
        :label="$t('setup_default_language_label')"
        item-text="name"
        item-value="key"
        :rules="[v => !!v || $t('required_field')]"
      >
        <template v-slot:selection="data">
          {{ $t(`languages_${data.item.key}`) }}
        </template>
        <template v-slot:item="data">
          {{ $t(`languages_${data.item.key}`) }}
        </template>
      </v-autocomplete>

      <div class="label font-weight-bold mt-6">
        {{$t('translations')}}
      </div>

      <div class="font-weight-light py-2">
        {{$t('setup_translations_description1')}}<br>
        {{$t('setup_translations_description2')}}
      </div>

      <v-autocomplete
        v-model="selectedTranslationLanguages"
        :items="languageDropdownWithoutDefaultLanguage"
        :search-input.sync="languageSearchInput"
        @change="languageSearchInput=''"
        item-text="name"
        item-value="key"
        chips
        :label="$t('select_languages')"
        :placeholder="$t('search_languages')"
        multiple
        class="my-3"
      >
        <template v-slot:selection="data">
          <v-chip
            v-bind="data.attrs"
            :input-value="data.selected"
            close
            @click="data.select"
            @click:close="removeLanguage(data.item)"
          >
            {{ data.item.name }}
          </v-chip>
        </template>
      </v-autocomplete>

      <div class="d-flex justify-end">
        <v-btn :disabled="!selectedDefaultLanguage" color="primary" raised @click="save">{{$t('continue')}}</v-btn>
      </div>
    </div>
  </div>
</template>

<script>

import Languages from '@/utils/languages'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'Setup',
  computed: {
    ...mapGetters(['initialized', 'defaultLanguage', 'editor', 'hasPublishedVersion', 'companyLanguages']),

    languageDropdown () {
      const collator = new Intl.Collator(this.$i18n.locale)
      const sorted = Languages.list.slice().sort((a, b) =>
        collator.compare(this.$t(`languages_${a.key}`), this.$t(`languages_${b.key}`))
      )

      const englishIndex = sorted.findIndex(lang => lang.key === 'en')
      if (englishIndex > 0) {
        const english = sorted.splice(englishIndex, 1)[0]
        sorted.unshift(english)
      }
      return sorted
    },

    languageDropdownWithoutDefaultLanguage () {
      return this.languageDropdown.filter((lang) => lang.key !== this.selectedDefaultLanguage)
    }
  },
  data () {
    return {
      languageSearchInput: '',
      selectedDefaultLanguage: '',
      selectedTranslationLanguages: []
    }
  },
  methods: {
    ...mapMutations({
      setDefaultLanguage: 'SET_DEFAULT_LANGUAGE',
      setEnabledLanguages: 'SET_ENABLED_LANGUAGES',
      approveLanguage: 'APPROVE_LANGUAGE'
    }),

    removeLanguage (lang) {
      this.selectedTranslationLanguages = this.selectedTranslationLanguages.filter((l) => l !== lang.key)
    },

    async save () {
      this.setDefaultLanguage(this.selectedDefaultLanguage)

      if (!this.selectedTranslationLanguages.includes(this.selectedDefaultLanguage)) {
        this.selectedTranslationLanguages.push(this.selectedDefaultLanguage)
      }
      this.setEnabledLanguages(this.selectedTranslationLanguages)
      this.$router.push({ name: 'config-editor' })
    }
  },
  async created () {
    await this.$store.dispatch('loadCompanyLanguages')
    this.selectedDefaultLanguage = this.companyLanguages?.[0] || ''
    this.selectedTranslationLanguages = this.companyLanguages || []
  }
}

</script>

<style lang="sass" scoped>

@import '@/variables.sass'

.setup-ctn
  margin-top: 50px
  width: 70%
  margin-left: auto
  margin-right: auto

.label
  text-transform: uppercase
  color: $label-grey

</style>
